// WeatherTemperature.js
import React, { useContext } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ReferenceLine,
} from 'recharts';
import { format, isSameHour } from 'date-fns';
import './weatherTemperature.scss';
import Tooltip from '../../../../atomicDesign/molecules/ChartToolTip';
import AdminContext from '../../../../context/admin';
import MuiFaIcon from '../../../../atomicDesign/atoms/MuiFaIcon';

const WeatherTemperature = ({ data = [], height }) => {
  const { language } = useContext(AdminContext);
  if (!data || data.length === 0) {
    return (
      <div className="no-data">
        {language?.eosDashboard?.weatherTemperature?.noData ??
          'No weather data available'}
      </div>
    );
  }

  const tempValues = data.map((i) => i.air_temperature);
  const avgTemp =
    tempValues.length > 0
      ? tempValues.reduce((sum, temp) => sum + temp, 0) / tempValues.length
      : 0;
  const minTemp = tempValues.length > 0 ? Math.min(...tempValues) : 0;
  const maxTemp = tempValues.length > 0 ? Math.max(...tempValues) : 0;

  const currentHourData = data.find((entry) =>
    isSameHour(new Date(entry.timestamp), new Date())
  );
  const currentHourTimestamp = currentHourData?.timestamp;
  const currentTemp =
    currentHourData?.air_temperature ||
    (tempValues.length > 0 ? tempValues[tempValues.length - 1] : 0);

  const yMinTemp = Math.floor(Math.min(...tempValues) - 1);
  const yMaxTemp = Math.ceil(Math.max(...tempValues) + 1);

  // Calculate temperature trend similar to KPI logic
  let tempDirection = 'right';
  let tempTrendColor = '#000000'; // Black for stable

  // Look at next few hours to determine trend
  const now = new Date();
  const futureData = data.filter((item) => new Date(item.timestamp) > now);

  if (futureData.length > 0) {
    // Get next 3 hours of data
    const next3Hours = futureData.slice(0, 3);
    if (next3Hours.length > 0) {
      const futureAvg =
        next3Hours.reduce((sum, item) => sum + item.air_temperature, 0) /
        next3Hours.length;
      const tempChange = futureAvg - currentTemp;

      if (Math.abs(tempChange) < 0.5) {
        // Stable
        tempDirection = 'right';
        tempTrendColor = '#000000'; // Black
      } else if (tempChange > 0) {
        // Rising
        tempDirection = 'up';
        tempTrendColor = '#ef4444'; // Red
      } else {
        // Falling
        tempDirection = 'down';
        tempTrendColor = '#3b82f6'; // Blue
      }
    }
  }

  const tempColor = '#3b82f6';
  const markerDotStyle = {
    r: 5,
    fill: tempColor,
    stroke: '#ffffff',
    strokeWidth: 2,
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
  };

  const processedData = data.map((item) => ({
    ...item,
    timestamp: item.timestamp,
    air_temperature: item.air_temperature,
    current_marker:
      item.timestamp === currentHourTimestamp ? item.air_temperature : null,
  }));

  const CustomWeatherTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    const originalDataPoint = data.find((item) => item.timestamp === label);
    if (!originalDataPoint) return null;

    const tooltipText = language?.eosDashboard?.weatherTemperature?.tooltip;

    const hideIfZero = (value) => (value === 0 ? undefined : value);

    const rows = [
      {
        label: tooltipText?.temperature ?? 'Temperature',
        value: originalDataPoint.air_temperature,
        unit: '°C',
      },
      {
        label: tooltipText?.wind ?? 'Wind',
        value: hideIfZero(originalDataPoint.wind_speed),
        unit: ' m/s',
      },
      {
        label: tooltipText?.windDirection ?? 'Wind direction',
        value: hideIfZero(originalDataPoint.wind_from_direction),
        unit: '°',
      },
      {
        label: tooltipText?.pressure ?? 'Pressure',
        value: hideIfZero(originalDataPoint.air_pressure_at_sea_level),
        unit: ' hPa',
      },
      {
        label: tooltipText?.cloudCover ?? 'Cloud cover',
        value: hideIfZero(originalDataPoint.cloud_area_fraction),
        unit: '%',
      },
      {
        label: tooltipText?.fog ?? 'Fog',
        value: hideIfZero(originalDataPoint.fog_area_fraction),
        unit: '%',
      },
      {
        label: tooltipText?.uvIndex ?? 'UV Index',
        value: hideIfZero(originalDataPoint.ultraviolet_index_clear_sky),
        unit: '',
      },
      {
        label: tooltipText?.precipitation ?? 'Precipitation next hour',
        value: hideIfZero(originalDataPoint.precipitation_amount),
        unit: ' mm',
      },
    ].filter((row) => Number.isFinite(row.value));

    return (
      <Tooltip>
        <Tooltip.Time>{format(new Date(label), 'MMM dd, HH:mm')}</Tooltip.Time>

        {originalDataPoint.conditions && (
          <Tooltip.Section>
            <Tooltip.Row
              label={tooltipText?.conditions ?? 'Conditions'}
              value={originalDataPoint.conditions}
            />
          </Tooltip.Section>
        )}

        {rows.length > 0 && (
          <Tooltip.Section>
            {rows.map((row) => (
              <Tooltip.Row
                key={row.label}
                label={row.label}
                value={`${row.value}${row.unit}`}
              />
            ))}
          </Tooltip.Section>
        )}
      </Tooltip>
    );
  };

  return (
    <div className="weather-chart-container">
      <div className="chart-header">
        <div className="chart-title-area">
          <div className="chart-title">
            <MuiFaIcon
              name="fas fa-cloud-sun chart-icon"
              className="chart-icon"
              aria-hidden="true"
            />
            {language?.eosDashboard?.weatherTemperature?.headerTitle ??
              'Weather Forecast'}
          </div>
          <p className="chart-subtitle">
            {language?.eosDashboard?.weatherTemperature?.headerSubtitle ??
              'Forecast'}
          </p>
        </div>

        <div className="chart-right-section">
          <div className="weather-stats-box">
            <div className="stat-item">
              <div className="stat-label">
                {language?.eosDashboard?.weatherTemperature?.stats?.min ??
                  'Min'}
              </div>
              <div className="stat-value">{minTemp.toFixed(1)}°C</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">
                {language?.eosDashboard?.weatherTemperature?.stats?.avg ??
                  'Avg'}
              </div>
              <div className="stat-value">{avgTemp.toFixed(1)}°C</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">
                {language?.eosDashboard?.weatherTemperature?.stats?.max ??
                  'Max'}
              </div>
              <div className="stat-value">{maxTemp.toFixed(1)}°C</div>
            </div>
            <div className="stat-item current">
              <div className="stat-label">
                {language?.eosDashboard?.weatherTemperature?.stats?.current ??
                  'Current'}
              </div>
              <div className="stat-value">{currentTemp.toFixed(1)}°C</div>
            </div>
          </div>
        </div>
      </div>

      <div className="chart-content" style={{ height: height ?? 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={processedData}
            margin={{ top: 10, right: 5, left: 25, bottom: 0 }}
          >
            <defs>
              <linearGradient id="tempGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.2} />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid
              stroke="#f1f5f9"
              strokeDasharray="0"
              vertical={false}
              horizontal={true}
              opacity={0.7}
            />

            <XAxis
              dataKey="timestamp"
              tickFormatter={(tick) => format(new Date(tick), 'HH:mm')}
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#64748b', fontSize: 11, fontWeight: 500 }}
              tickMargin={8}
            />

            <YAxis
              domain={[yMinTemp, yMaxTemp]}
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#64748b', fontSize: 11, fontWeight: 500 }}
              tickFormatter={(value) => `${Math.round(value)}°C`}
              tickMargin={8}
              width={50}
            />

            <RechartsTooltip content={<CustomWeatherTooltip />} />

            {currentHourTimestamp && (
              <ReferenceLine
                x={currentHourTimestamp}
                stroke="#64748b"
                strokeWidth={1}
                opacity={0.6}
              />
            )}

            <ReferenceLine
              y={0}
              stroke="#94a3b8"
              strokeDasharray="2 2"
              opacity={0.7}
            />

            <Area
              type="monotone"
              dataKey="air_temperature"
              stroke="#3b82f6"
              strokeWidth={2}
              fill="url(#tempGradient)"
              dot={false}
              connectNulls
              activeDot={false}
            />

            <Line
              type="monotone"
              dataKey="air_temperature"
              stroke="transparent"
              dot={false}
              activeDot={{
                r: 4,
                fill: tempColor,
                stroke: '#ffffff',
                strokeWidth: 2,
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
              }}
            />

            <Line
              type="monotone"
              dataKey="current_marker"
              stroke="none"
              dot={markerDotStyle}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// stats for use in the parent component
WeatherTemperature.getStats = (data) => {
  if (!data || data.length === 0) return { min: 0, avg: 0, max: 0 };

  const tempValues = data.map((i) => i.air_temperature);
  return {
    min: Math.min(...tempValues),
    avg: tempValues.reduce((sum, temp) => sum + temp, 0) / tempValues.length,
    max: Math.max(...tempValues),
  };
};

export default WeatherTemperature;
